import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  Shield,
  Globe,
  Zap,
  Headphones,
  Radio,
  Phone,
  Satellite,
  Home,
  Building2,
  Truck,
  Tablet,
  HardHat,
  Wifi,
} from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { SchedulingForm } from "@/components/SchedulingForm";

const stats = [
  { label: "Uptime", value: "99.9%", icon: Shield },
  { label: "Countries", value: "200+", icon: Globe },
  { label: "Failover", value: "<10ms", icon: Zap },
  { label: "Support", value: "24/7 US", icon: Headphones },
];

const services = [
  // Field Solutions
  { icon: Tablet, label: "Field Tablets", path: "/field-tablets-mobile-internet" },
  { icon: Truck, label: "Fleet Tracking", path: "/fleet-asset-tracking" },
  { icon: HardHat, label: "Jobsite", path: "/jobsite-connectivity" },
  { icon: Radio, label: "Push-to-Talk", path: "/push-to-talk" },
  // Enterprise Connectivity
  { icon: Building2, label: "Business Internet", path: "/business-internet-solutions" },
  { icon: Shield, label: "Failover", path: "/failover-solutions" },
  { icon: Wifi, label: "Fixed Wireless", path: "/fixed-wireless-solutions" },
  { icon: Globe, label: "IoT", path: "/internet-of-things-iot" },
  { icon: Phone, label: "POTS", path: "/pots-replacement-solutions" },
  { icon: Satellite, label: "Starlink", path: "/starlink-business-internet" },
  // Home
  { icon: Home, label: "Home Internet", path: "/home-internet" },
];

export const HomeHero = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const isMobile = useIsMobile();
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    const handleOpenForm = () => setIsFormOpen(true);
    window.addEventListener("openHeroForm", handleOpenForm);
    return () => window.removeEventListener("openHeroForm", handleOpenForm);
  }, []);



  // Stable particle layout (avoid re-randomizing every render)
  const particles = useMemo(() => {
    const count = isMobile ? 10 : 16;
    return Array.from({ length: count }, (_, i) => ({
      key: i,
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
      duration: 3 + Math.random() * 2,
      delay: Math.random() * 2,
      drift: 24 + Math.random() * 12,
    }));
  }, [isMobile]);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-background via-background to-primary/5">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        {!reduceMotion &&
          particles.map((p) => (
            <motion.div
              key={p.key}
              className="absolute w-1 h-1 bg-primary/20 rounded-full"
              style={{ left: p.left, top: p.top }}
              animate={{ y: [0, -p.drift, 0], opacity: [0.2, 0.5, 0.2] }}
              transition={{ duration: p.duration, repeat: Infinity, delay: p.delay }}
            />
          ))}
      </div>

      <div className="container mx-auto px-4 py-20 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center lg:text-left"
          >
            <p className="text-sm font-semibold text-primary mb-4 tracking-wider uppercase">
              All SimNet Solutions
            </p>

            <h1
              className="text-4xl md:text-5xl lg:text-[3.5rem] font-bold mb-6 text-white leading-tight"
            >
              Complete <span className="text-primary">Business Connectivity Solutions</span>
            </h1>

            <p className="text-lg md:text-xl text-gray-200 mb-8 leading-relaxed font-medium">
              One provider for your office, field crews, vehicles, job sites, and enterprise needs.
            </p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-12"
            >
              <Button size="lg" onClick={() => setIsFormOpen(true)} className="text-lg px-8">
                Get Connected
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="text-lg px-8"
                onClick={() =>
                  document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })
                }
              >
                View All Solutions
              </Button>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="grid grid-cols-2 sm:grid-cols-4 gap-4"
            >
              {stats.map((stat, index) => (
                <div key={index} className="flex flex-col items-center lg:items-start gap-2 p-4 glass-card rounded-lg">
                  <stat.icon className="w-5 h-5 text-primary" />
                  <div className="text-2xl font-bold text-foreground">{stat.value}</div>
                  <div className="text-sm text-muted-foreground">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right Column - Services Grid */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="relative w-full max-w-[640px] mx-auto"
          >
            <div className="grid grid-cols-4 gap-3 md:gap-4">
              {services.map((service, index) => {
                const pulseDelay = index * 1.5;
                return (
                  <motion.div
                    key={service.label}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 + index * 0.05 }}
                  >
                    <Link
                      to={service.path}
                      className="group relative flex flex-col items-center justify-center gap-2 p-3 md:p-4 h-[110px] md:h-[120px] rounded-xl border border-primary/20 bg-primary/5 backdrop-blur-sm transition-all duration-300 hover:border-primary hover:bg-primary/10 hover:scale-105"
                      style={{
                        boxShadow: "0 0 0px rgba(0, 191, 99, 0)",
                      }}
                    >
                      <motion.div
                        className="absolute inset-0 rounded-xl pointer-events-none"
                        animate={
                          reduceMotion
                            ? undefined
                            : {
                                boxShadow: [
                                  "0 0 0px rgba(0, 191, 99, 0)",
                                  "0 0 22px rgba(0, 191, 99, 0.55)",
                                  "0 0 0px rgba(0, 191, 99, 0)",
                                ],
                              }
                        }
                        transition={
                          reduceMotion
                            ? undefined
                            : { duration: 12, repeat: Infinity, delay: pulseDelay, ease: "easeInOut" }
                        }
                      />
                      <div className="relative flex flex-col items-center gap-1.5 text-primary transition-all duration-300 group-hover:[filter:drop-shadow(0_0_12px_rgba(0,191,99,0.9))]">
                        <service.icon size={32} className="shrink-0" />
                        <span className="text-[10px] md:text-xs font-semibold text-center leading-tight">
                          {service.label}
                        </span>
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>

      <SchedulingForm isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} />
    </section>
  );
};

